import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import {
  ChangeCurrentCpId,
  ClearCurrentCp,
  CpActionTypes,
  CpResetData,
  CpsLoad,
  CpsLoadSuccess,
  CpUpdate,
  CpUpdateSuccess,
  CreateMcps,
  CreateMcpsSuccess,
  DeleteCurrentCp,
  LoadCurrentCp,
  LoadCurrentCpSuccess,
  LoadMcpsForCp,
  LoadMcpsForCpSuccess,
  ProjectTreeLoad,
  ResetCpItems,
  ClearCurrentMcp,
} from '@completion/actions';
import { DocumentClear } from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentProject } from '@completion/selectors';
import { CpService } from '@completion/services';
import { CommissioningPackage, Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class CommPkgsEffects {
  constructor(
    private actions$: Actions,
    private cpService: CpService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  clearCurrentCp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ClearCurrentCp>(CpActionTypes.ClearCurrentCp),
      map(() => new ClearCurrentMcp()),
    ),
  );

  loadCPs$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpsLoad>(CpActionTypes.CpsLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CommissioningPackage[]>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'getCommPkgs',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.taskId],
        onSuccess: (response) => new CpsLoadSuccess(response),
      }),
    ),
  );

  loadCP$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCurrentCp>(CpActionTypes.LoadCurrentCp),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource<CommissioningPackage>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'getCp',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new LoadCurrentCpSuccess(response),
      }),
    ),
  );

  updateCp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpUpdate>(CpActionTypes.CpUpdate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CommissioningPackage>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'updateCp',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.cp],
        onSuccess: (response) => new CpUpdateSuccess(response),
      }),
    ),
  );

  loadMcpsForCp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadMcpsForCp>(CpActionTypes.LoadMcpsForCp),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'getAllMcps',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new LoadMcpsForCpSuccess(response as any),
      }),
    ),
  );

  createMcps$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateMcps>(CpActionTypes.CreateMcps),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'createMcps',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.mcps],
        onSuccess: (response, [_, __, ___, ____]) => new CreateMcpsSuccess(response as any, ____ as number),
      }),
    ),
  );

  deleteCP$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteCurrentCp>(CpActionTypes.DeleteCurrentCp),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'deleteCp',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: () => new ProjectTreeLoad(),
      }),
    ),
  );

  refetchCpData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpResetData>(CpActionTypes.CpResetData),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'refetchCpData',
        serviceArgs: ([_, currentProject, currentCpId]) => [currentProject.id, currentCpId],
        onSuccess: () => null,
      }),
    ),
  );

  resetCpItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetCpItems>(CpActionTypes.ResetCpItems),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number> as Observable<number>,
      ),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'resetCpItems',
        serviceArgs: ([action, currentProject, currentCpId]) => [currentProject.id, currentCpId, action.reset],
        onSuccess: () => null,
      }),
    ),
  );

  // When CP changes, clear current document (load deferred until Commissioning Procedure tab is opened)
  reloadDocumentOnCpChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ChangeCurrentCpId>(CpActionTypes.ChangeCurrentCpId),
      map(() => new DocumentClear()),
    ),
  );

  // When CP is cleared, also clear current document
  clearDocumentOnCpClear$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ClearCurrentCp>(CpActionTypes.ClearCurrentCp),
      map(() => new DocumentClear()),
    ),
  );
}
